export const TOUR_LEADER_JOB_STATUSES = ['Draft', 'Published', 'Unpublished', 'Closed']

export const TOUR_LEADER_APPLICATION_STATUSES = [
  'Pending',
  'Viewed',
  'Contacting',
  'Accepted',
  'Rejected',
  'Cancelled',
]

const TOUR_LEADER_APPLICATION_MANAGEMENT_STATUSES = ['Viewed', 'Contacting', 'Accepted', 'Rejected']

const EDITABLE_JOB_FIELDS = [
  'title',
  'roleType',
  'category',
  'destination',
  'meetingPlace',
  'startDate',
  'endDate',
  'applicationDeadline',
  'salaryAmount',
  'currency',
  'compensationType',
  'quota',
  'tags',
  'summary',
  'description',
  'requirements',
]

function normalizedEnum(value, allowedValues, fallback = '') {
  const normalized = String(value == null ? '' : value)
    .trim()
    .toLowerCase()
  return allowedValues.find(item => item.toLowerCase() === normalized) || fallback
}

function boundedInteger(value, fallback, minimum, maximum) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.min(maximum, Math.max(minimum, Math.trunc(parsed)))
}

function trimmed(value, maximumLength) {
  return String(value == null ? '' : value)
    .trim()
    .slice(0, maximumLength)
}

function normalizedDate(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''
}

export function normalizeJobStatus(status, fallback = 'Unknown') {
  return normalizedEnum(status, TOUR_LEADER_JOB_STATUSES, fallback)
}

export function normalizeApplicationStatus(status, fallback = 'Unknown') {
  return normalizedEnum(status, TOUR_LEADER_APPLICATION_STATUSES, fallback)
}

export function requireJobStatus(status) {
  const normalized = normalizedEnum(status, TOUR_LEADER_JOB_STATUSES)
  if (!normalized) throw new TypeError('tour_leader_job_status_invalid')
  return normalized
}

export function requireApplicationStatus(status) {
  const normalized = normalizedEnum(status, TOUR_LEADER_APPLICATION_MANAGEMENT_STATUSES)
  if (!normalized) throw new TypeError('tour_leader_application_status_invalid')
  return normalized
}

export function buildTourLeaderJobQuery(query, options = {}) {
  const result = {
    keyword: trimmed(query && query.keyword, 100),
    roleType: normalizedEnum(query && query.roleType, ['Leader', 'Guide', 'Both']),
    category: trimmed(query && query.category, 100),
    destination: trimmed(query && query.destination, 200),
    startDate: normalizedDate(query && query.startDate),
    endDate: normalizedDate(query && query.endDate),
    sort: ['recommended', 'latest', 'deadline', 'startDate', 'salaryDesc'].includes(
      query && query.sort
    )
      ? query.sort
      : 'latest',
    page: boundedInteger(query && query.page, 1, 1, 1000000),
    pageSize: boundedInteger(query && (query.pageSize || query.limit), 20, 1, 100),
  }

  if (options.includeStatus) {
    result.status = normalizedEnum(query && query.status, TOUR_LEADER_JOB_STATUSES)
  }
  if (options.includeAgency) {
    const agencyCode = trimmed(query && query.agencyCode, 50)
    result.agencyCode = /^[A-Za-z0-9_-]*$/.test(agencyCode) ? agencyCode : ''
  }

  return result
}

export function createEmptyTourLeaderJob() {
  return {
    id: null,
    targetAgencyCode: '',
    agencyCode: '',
    agencyName: '',
    title: '',
    roleType: 'Both',
    category: '',
    destination: '',
    meetingPlace: '',
    startDate: '',
    endDate: '',
    applicationDeadline: '',
    salaryAmount: 0,
    currency: 'TWD',
    compensationType: 'PerDay',
    quota: 1,
    tags: [],
    summary: '',
    description: '',
    requirements: '',
    isRecommended: false,
    recommendSort: 99,
    status: 'Draft',
  }
}

export function normalizeTourLeaderJob(job) {
  const source = job && typeof job === 'object' ? job : {}
  const normalized = Object.assign(createEmptyTourLeaderJob(), source)
  normalized.id = source.id === undefined ? source.jobId : source.id
  normalized.roleType = source.roleType || source.role || 'Both'
  normalized.meetingPlace = source.meetingPlace || source.location || ''
  normalized.startDate = source.startDate || source.workStartDate || ''
  normalized.endDate = source.endDate || source.workEndDate || ''
  normalized.quota = boundedInteger(source.quota || source.capacity, 1, 1, 999)
  normalized.status = normalizeJobStatus(source.status, source.status ? 'Unknown' : 'Draft')
  normalized.salaryAmount =
    String(normalized.compensationType).toLowerCase() === 'negotiable'
      ? null
      : Number(source.salaryAmount) || 0
  normalized.applicationCount = boundedInteger(source.applicationCount, 0, 0, 1000000)
  normalized.recommendSort = boundedInteger(source.recommendSort, 99, 0, 999999)
  normalized.isRecommended = source.isRecommended === true
  normalized.tags = Array.isArray(source.tags)
    ? source.tags
        .filter(tag => typeof tag === 'string')
        .map(tag => trimmed(tag, 30))
        .slice(0, 10)
    : []
  return normalized
}

export function toTourLeaderJobPayload(job, options = {}) {
  const source = job && typeof job === 'object' ? job : {}
  const payload = EDITABLE_JOB_FIELDS.reduce((result, field) => {
    result[field] = source[field]
    return result
  }, {})

  payload.title = trimmed(payload.title, 200)
  payload.roleType = trimmed(payload.roleType, 20)
  payload.category = trimmed(payload.category, 100)
  payload.destination = trimmed(payload.destination, 200)
  payload.meetingPlace = trimmed(payload.meetingPlace, 200)
  payload.summary = trimmed(payload.summary, 500)
  payload.description = trimmed(payload.description, 4000)
  payload.requirements = trimmed(payload.requirements, 4000)
  payload.currency = trimmed(payload.currency, 3).toUpperCase() || 'TWD'
  payload.compensationType = ['Total', 'PerDay', 'Negotiable'].includes(payload.compensationType)
    ? payload.compensationType
    : 'PerDay'
  payload.salaryAmount =
    payload.compensationType === 'Negotiable'
      ? null
      : Math.max(0, Number(payload.salaryAmount) || 0)
  payload.quota = boundedInteger(payload.quota, 1, 1, 999)
  payload.tags = Array.isArray(payload.tags)
    ? payload.tags
        .filter(tag => typeof tag === 'string')
        .map(tag => trimmed(tag, 30))
        .slice(0, 10)
    : []

  if (options.includeAdminFields) {
    payload.isRecommended = source.isRecommended === true
    payload.recommendSort = boundedInteger(source.recommendSort, 99, 0, 999999)
  }
  if (options.includeTargetAgency) {
    payload.targetAgencyCode = trimmed(source.targetAgencyCode, 50)
  }

  return payload
}

export function toTourLeaderJobEditor(job, options = {}) {
  const normalized = normalizeTourLeaderJob(job)
  const editor = Object.assign(createEmptyTourLeaderJob(), normalized)
  editor.targetAgencyCode = options.copyForAdmin ? normalized.agencyCode : ''
  if (options.copy) {
    editor.id = null
    editor.status = 'Draft'
    editor.title = `${normalized.title} - 副本`.slice(0, 200)
  }
  return editor
}

export function jobStatusLabel(status) {
  return (
    {
      Draft: '草稿',
      Published: '已發布',
      Unpublished: '已下架',
      Closed: '已結束',
    }[normalizeJobStatus(status)] || '未知'
  )
}

export function applicationStatusLabel(status) {
  return (
    {
      Pending: '待處理',
      Viewed: '已查看',
      Contacting: '聯繫中',
      Accepted: '已錄取',
      Rejected: '未錄取',
      Cancelled: '已取消',
    }[normalizeApplicationStatus(status)] || '未知'
  )
}

export function buildApplicantHunterRoute(application, action) {
  const source = application && typeof application === 'object' ? application : {}
  const memberUserId = Number(source.memberUserId || source.userId || source.applicantId)
  if (!Number.isInteger(memberUserId) || memberUserId <= 0) return null

  const safeAction = ['profile', 'contact', 'chat'].includes(action) ? action : 'profile'
  const query = { memberUserId: String(memberUserId), action: safeAction }
  const conversationId = source.conversationId || source.roomId
  if (conversationId) query.conversationId = String(conversationId)
  return { name: 'AgencyLeaderHunter', query }
}
